import { useEffect, useState } from "react";
import { Chip } from "@/components/Chip";
import {
  categorize,
  formatFecha,
  toneFor,
} from "@/lib/folio-analysis";
import type { ActoDetalle, Movimiento } from "@/lib/types";

export function ActoDrawer({
  folioId,
  movimiento,
  onClose,
}: {
  folioId: string;
  movimiento: Movimiento;
  onClose: () => void;
}) {
  const [data, setData] = useState<ActoDetalle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSub, setActiveSub] = useState(0);

  const tone = toneFor(categorize(movimiento));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setActiveSub(0);
    const qs = new URLSearchParams({
      control: movimiento.control,
      consecutivo: movimiento.consecutivo,
      fechaBoleta: movimiento.fechaBoletaSirjal,
    });
    fetch(
      `/api/folios/${encodeURIComponent(folioId)}/actos/${encodeURIComponent(movimiento.clave)}?${qs}`,
    )
      .then(async (r) => {
        const text = await r.text();
        let body: unknown = null;
        try {
          body = text ? JSON.parse(text) : null;
        } catch {
          /* ignore */
        }
        if (!r.ok) {
          const msg =
            (body as { upstream?: string; error?: string } | null)?.upstream ??
            (body as { error?: string } | null)?.error ??
            (r.status === 404
              ? "No se encontró el detalle del acto."
              : `Error ${r.status}`);
          throw new Error(msg);
        }
        return body as ActoDetalle;
      })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    folioId,
    movimiento.clave,
    movimiento.control,
    movimiento.consecutivo,
    movimiento.fechaBoletaSirjal,
  ]);

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const sections = data
    ? [
        { titulo: data.nombre, campos: data.campos },
        ...(data.subActos ?? []).map((s) => ({
          titulo: s.titulo,
          campos: s.campos,
        })),
      ]
    : [];

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={`Detalle del acto ${movimiento.clave}`}
    >
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
      />

      <div className="relative flex h-full w-full max-w-xl flex-col overflow-hidden bg-white shadow-elevated">
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Chip tone={tone}>{movimiento.clave}</Chip>
              <span className="text-xs text-slate-500">
                {formatFecha(movimiento.fechaRegistro)}
              </span>
            </div>
            <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900">
              {data?.nombre ?? movimiento.acto}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Folio{" "}
              <span className="font-mono text-slate-700">{folioId}</span> ·
              Control{" "}
              <span className="font-mono text-slate-700">
                {movimiento.control}/{movimiento.consecutivo}
              </span>
              {movimiento.escrituraOficio &&
                movimiento.escrituraOficio !== "." && (
                  <>
                    {" "}
                    · Escritura{" "}
                    <span className="font-mono text-slate-700">
                      {movimiento.escrituraOficio}
                    </span>
                  </>
                )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="ml-4 grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-4 w-4"
              aria-hidden
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-10 animate-pulse rounded-lg bg-slate-100"
                />
              ))}
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}
          {data && (
            <>
              {sections.length > 1 && (
                <div className="mb-5 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                  {sections.map((s, i) => {
                    const isActive = activeSub === i;
                    return (
                      <button
                        key={s.titulo}
                        type="button"
                        onClick={() => setActiveSub(i)}
                        className={[
                          "rounded-md px-3 py-1.5 text-xs font-semibold transition",
                          isActive
                            ? "bg-white text-slate-900 shadow-card"
                            : "text-slate-600 hover:text-slate-900",
                        ].join(" ")}
                      >
                        {s.titulo}
                      </button>
                    );
                  })}
                </div>
              )}

              <dl className="divide-y divide-slate-100 rounded-xl border border-slate-200">
                {(() => {
                  const seen = new Set<string>();
                  return sections[activeSub].campos.map(([k, v], i) => {
                    const isGroupStart = i > 0 && seen.has(k);
                    if (isGroupStart) seen.clear();
                    seen.add(k);
                    return (
                      <div
                        key={`${k}-${i}`}
                        className={[
                          "grid grid-cols-5 gap-3 px-4 py-3 text-sm",
                          isGroupStart ? "border-t-4 border-t-slate-100" : "",
                        ].join(" ")}
                      >
                        <dt className="col-span-2 text-xs font-medium uppercase tracking-wider text-slate-500">
                          {k}
                        </dt>
                        <dd className="col-span-3 text-slate-800">
                          {v && v.length > 0 ? (
                            v
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </dd>
                      </div>
                    );
                  });
                })()}
              </dl>
            </>
          )}
        </div>

        <div className="border-t border-slate-200 bg-slate-50 px-6 py-3 text-right">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
